/**
 * @file: InboxOverlayHost.tsx
 *
 * Mounts the inbox's transient panels — chat search, inbox settings, the 1:1
 * contacts picker, and the multi-select member picker — and shows the one that
 * `overlay$` names. The panels are mutually exclusive (only one open at a
 * time), which is exactly what the single `overlay$` represents; each panel is
 * its own component deriving `visible` from the active kind, so there's no
 * switch-case here.
 *
 * Mounted once for BOTH layouts: each panel's `BottomPanel` is a sheet on
 * mobile and a centred card on desktop.
 *
 * EARLY RESOLVE: opening a chat by contact/room resolves the room HERE (behind
 * a spinner) BEFORE selecting it — on success the detail surface receives a
 * `resolved` intent (desktop selects it as the active thread, mobile navigates); on
 * failure it alerts and does nothing (no half-resolved room ever renders).
 * Thread picks are already resolved (the row carries the room), so they skip
 * the spinner.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { ChatSearchPanel } from '@euler/app/flows/chat/components/ChatSearchPanel';
import { ContactPickerPanel } from '@euler/app/flows/chat/components/ContactPickerPanel';
import { MemberPickerPanel } from '@euler/app/flows/chat/components/MemberPickerPanel';
import { ChatConversationHandle } from '@euler/app/flows/chat/lib/conversation/handle';
import type { ConversationHandleQuery } from '@euler/app/flows/chat/lib/conversation/types';
import { conversationHandleForThread } from '@euler/app/flows/chat/lib/conversation/types';
import { useOpenChatConversation } from '@euler/app/flows/chat/lib/navigation/openChatConversation';
import { useChatThreads } from '@euler/app/flows/chat/lib/threads/context';
import {
  ChatContactPickerParams,
  ChatPickerDoneResult,
  ChatSearchPick,
} from '@euler/app/flows/chat/lib/types';
import { resolveConversationHandle } from '@euler/app/flows/chat/utils/resolveConversationHandle';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useAlert } from '@euler/functions/ux/useAlert';
import { ChatContact } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useInboxContext } from '../context';
import { inboxConversation } from '../types';
import { InboxSettingsPanel } from './InboxSettingsPanel';

export const InboxOverlayHost: FC = memo(() => {
  const { overlay$, layoutMode$, activeThread$, infoOpen$, nonce$ } =
    useInboxContext();
  const [overlay] = useBehaviorSubject(overlay$);
  const openChatFlow = useOpenChatConversation();
  const { chatService } = useServiceFactory();
  const { refreshChatThreads } = useChatThreads();
  const currentUser = useCurrentUser();
  const selfName = currentUser?.user?.name ?? null;
  const alert = useAlert();
  const loading = useAppLoading();

  const close = useCallback(() => overlay$.next(null), [overlay$]);

  // Show a resolved conversation: desktop selects it into the detail pane;
  // mobile has no pane, so it pushes the app-level chat flow (wrapping the
  // handle in a resolved intent — the conversation's route param is an intent).
  const showConversation = useCallback(
    (handle: ChatConversationHandle) => {
      if (layoutMode$.getValue() === 'desktop') {
        activeThread$.next(inboxConversation(handle));
        infoOpen$.next(false);
      } else {
        openChatFlow({ kind: 'resolved', handle });
      }
    },
    [layoutMode$, activeThread$, infoOpen$, openChatFlow],
  );

  // Resolve a pending open (contact/room) BEFORE selecting it: `showWithAction`
  // shows the app loading indicator (after a short delay, so a fast resolve
  // doesn't flash) and hides it when done. On failure we alert and do nothing —
  // navigation only happened on success, so the inbox stays where it was and
  // the surface never half-opens.
  const openConversation = useCallback(
    (query: ConversationHandleQuery) =>
      loading.showWithAction(
        async () => {
          const handle = await resolveConversationHandle(chatService, query);
          overlay$.next(null);
          showConversation(handle);
        },
        {
          onError: e => {
            console.error('resolve conversation failed', e);
            return alert({ title: '无法打开会话', message: '请稍后重试' });
          },
        },
      ),
    [loading, chatService, overlay$, showConversation, alert],
  );

  const openContact = useCallback(
    (contact: ChatContact) => {
      void openConversation({
        kind: 'contact',
        peer: {
          platformUserId: contact.platformUserId,
          appId: contact.appId,
          appScope: contact.appScope,
          name: contact.name,
          avatar: contact.avatar,
        },
      });
    },
    [openConversation],
  );

  const onSearchPick = useCallback(
    (pick: ChatSearchPick) => {
      if (pick.kind === 'contact') {
        openContact(pick.contact);
        return;
      }
      // A thread pick is already resolved (the row carries the room).
      overlay$.next(null);
      showConversation(conversationHandleForThread(pick.thread, selfName));
    },
    [openContact, overlay$, showConversation, selfName],
  );

  // The member picker is opened only from the desktop room-info column. A new
  // group resolves + opens as a conversation; an invite refetches the open
  // room's members.
  const onPickerDone = useCallback(
    (result: ChatPickerDoneResult) => {
      overlay$.next(null);
      if (result.kind === 'new-group') {
        void openConversation({ kind: 'room', roomId: result.roomId });
      } else if (result.kind === 'invite') {
        nonce$.next(nonce$.getValue() + 1);
      }
      void refreshChatThreads();
    },
    [overlay$, openConversation, nonce$, refreshChatThreads],
  );

  // Retain the last picker params so the member panel keeps its content through
  // the close animation (BottomPanel stays mounted until the exit finishes).
  const [pickerParams, setPickerParams] =
    useState<ChatContactPickerParams | null>(null);
  useEffect(() => {
    if (overlay?.kind === 'picker') setPickerParams(overlay.params);
  }, [overlay]);

  const kind = overlay?.kind;
  return (
    <>
      <ChatSearchPanel
        visible={kind === 'search'}
        onPick={onSearchPick}
        onClose={close}
      />
      <InboxSettingsPanel visible={kind === 'settings'} onClose={close} />
      <ContactPickerPanel
        visible={kind === 'contacts'}
        onSelect={openContact}
        onClose={close}
      />
      {pickerParams ? (
        <MemberPickerPanel
          visible={kind === 'picker'}
          params={pickerParams}
          onDone={onPickerDone}
          onClose={close}
        />
      ) : null}
    </>
  );
});
